import { Link } from '@tanstack/react-router';
import { CircleSlashIcon } from 'lucide-react';

import { THRESHOLD_TEXT } from '@/features/workspace-usage/thresholdStyles';
import { isReached, type LimitState, type ResolvedLimits } from '../limitState';

export interface BoardLimitNoticeProps {
  workspaceId: string;
  workspaceName: string;
  /** The board's own workflow, named only when it is the scope responsible. */
  workflowName: string;
  /** The one answer, resolved for this workspace and this board's workflow. */
  limits: ResolvedLimits;
}

/** Whether this reader may open the usage figures at all. */
function mayReadFigures(...states: LimitState[]): boolean {
  // The figures having answered for any scope is itself the proof. There is no
  // separate permission to read: the service decides by answering or refusing
  // (research R4), and offering a link to a page that returns 403 is a dead end
  // dressed as a way on (FR-031).
  return states.some((state) => state.known && state.source === 'figures');
}

/**
 * A board that cannot start runs says so, before anyone touches it (spec 101 US1).
 *
 * The problem this fixes, in the reporter's words: "Now i can only see it on task
 * card when move it to next step." A workspace or workflow at its spend limit
 * looked entirely normal until a move had already been made and the run it should
 * have started was quietly refused.
 *
 * Stated as fact rather than hedged. The service computes the threshold a client
 * sees with the same comparison its dispatch gate uses, and its own source comment
 * gives the reason: a workspace reported as merely approaching while its members
 * are already blocked would contradict what they see. "Limit reached" and "the
 * next run will be refused" are one condition, so this may say so plainly.
 *
 * Absent unless a limit is actually reached. A line that is present most of the
 * time is a line nobody reads on the day it matters.
 */
export function BoardLimitNotice({
  workspaceId,
  workspaceName,
  workflowName,
  limits,
}: BoardLimitNoticeProps) {
  const workspaceReached = isReached(limits.workspace);
  const workflowReached = isReached(limits.workflow);
  if (!workspaceReached && !workflowReached) return null;

  /*
    The workspace wins when both are reached (FR-004). It is the wider cause, and
    raising only the workflow's cap would leave the board exactly as stopped —
    sending a reader to do work that changes nothing is worse than saying less.
  */
  const scopeName = workspaceReached ? workspaceName : workflowName;
  const scopeWord = workspaceReached ? 'workspace' : 'workflow';

  return (
    <div
      // A status region, so it is announced when it appears rather than only
      // drawn (FR-007). Not an alert: nothing here is an emergency and nothing
      // is interrupted, the board simply will not start new work.
      role="status"
      // Named, because a board carries other live regions — the keyboard's
      // "where you landed" announcement among them — and a region a test or a
      // screen-reader rotor cannot tell from those is one nobody can address.
      aria-label="Spend limit"
      className="border-board-state-failed/40 bg-board-state-failed/10 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-xs"
    >
      <CircleSlashIcon
        aria-hidden="true"
        className={`${THRESHOLD_TEXT.exhausted} size-4 shrink-0`}
        strokeWidth={1.5}
      />
      {/*
        The state in words as well as in colour, always (FR-021) — and the colour
        itself is the usage page's own for this threshold, so the board and the
        figures cannot describe one state in two ways.
      */}
      <span className={`${THRESHOLD_TEXT.exhausted} font-medium`}>
        No runs will start
      </span>
      <span className="text-muted-foreground">
        The {scopeWord} {scopeName} has reached its spend limit. Moving a task
        still works, but no run starts until the limit resets or is raised.
      </span>
      {mayReadFigures(limits.workspace, limits.workflow) ? (
        <Link
          to="/workspaces/$workspaceId/ai-usage/spend"
          params={{ workspaceId }}
          className="text-foreground font-medium underline underline-offset-2"
        >
          View usage
        </Link>
      ) : (
        <span className="text-muted-foreground">
          Ask an administrator to raise it.
        </span>
      )}
    </div>
  );
}
